"use client";

import Script from "next/script";
import {
  ANALYTICS_GTM_FLAG,
  ANALYTICS_SEGMENT_FLAG,
  getGoogleTagManagerId,
  getSegmentWriteKey,
  isFeatureEnabled,
  loadConfig,
  type AppConfig,
} from "@project-x/config";
import type { AnalyticsConsentChoice } from "./analyticsConsent";
import { hasAcceptedAnalyticsConsent } from "./analyticsConsent";

/** Production Web GTM container. The Google tag destination lives in this container. */
export const PROD_GTM_WEB_CONTAINER_ID = "GTM-WRQ8G7RF";
/** Canonical GA4 Web measurement ID configured as the GTM Google tag destination, not a second gtag.js. */
export const PROD_GA4_WEB_MEASUREMENT_ID = "G-1R5WNMYEBW";

interface AnalyticsScriptsProps {
  readonly config?: AppConfig;
  /** Test override. When omitted, the stored consent choice is used. */
  readonly consent?: AnalyticsConsentChoice | null;
}

interface AnalyticsState {
  readonly segment: {
    readonly enabled: boolean;
    readonly writeKey?: string;
  };
  readonly gtm: {
    readonly enabled: boolean;
    readonly id?: string;
  };
}

export const resolveAnalyticsState = (config: AppConfig): AnalyticsState => {
  const segmentWriteKey = getSegmentWriteKey(config);
  const gtmId = getGoogleTagManagerId(config);

  const segmentEnabled = Boolean(segmentWriteKey) && isFeatureEnabled(config, ANALYTICS_SEGMENT_FLAG);
  const gtmEnabled = Boolean(gtmId) && isFeatureEnabled(config, ANALYTICS_GTM_FLAG);

  return {
    segment: {
      enabled: segmentEnabled,
      writeKey: segmentWriteKey,
    },
    gtm: {
      enabled: gtmEnabled,
      id: gtmId,
    },
  };
};

const renderGtmBootstrap = (id: string) => {
  return `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${id}');
  `;
};

export const AnalyticsScripts = ({ config = loadConfig(), consent }: AnalyticsScriptsProps) => {
  const allowed = consent === undefined ? hasAcceptedAnalyticsConsent() : consent === "accepted";
  const { segment, gtm } = resolveAnalyticsState(config);

  if (!allowed || (!segment.enabled && !gtm.enabled)) {
    return null;
  }

  return (
    <>
      {segment.enabled && segment.writeKey ? (
        <Script
          id="segment-loader"
          src={`https://cdn.segment.com/analytics.js/v1/${segment.writeKey}/analytics.min.js`}
          strategy="afterInteractive"
        />
      ) : null}
      {gtm.enabled && gtm.id ? (
        <Script id="gtm-loader" strategy="afterInteractive">
          {renderGtmBootstrap(gtm.id)}
        </Script>
      ) : null}
    </>
  );
};
